import { useCallback, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import CustomSelect from "src/generator/_Selects/CustomSelect/CustomSelect";
import CustomInput from "src/generator/CustomInput/CustomInput";
import { getNextInput } from "src/generator/utils/DpsPopulationHelpers";
import { ICol } from "src/interfaces/Logbooks";
import { type IOzoneItem } from "src/pages/Settings/YachtSetup/OzoneDepletingSubstances/OzoneDepletingSubstances.types";
import { useDispatch, useSelector } from "src/redux/hooks";
import { addNewDynamicRow } from "src/redux/slices/generatorSlice";
import { validateDate } from "src/utils";

import { getFilledRow, getNewEmptyRow } from "./DpsEquipInput.utils";

interface Props {
  id: string;
  value: string;
  required: boolean;
  sectionIndex?: number;
  rowIndex?: number;
  sectionData?: {
    dynamic?: boolean;
    className?: string;
    [key: string]: any;
  };
}

const DpsEquipInput = ({
  id,
  value,
  required,
  sectionIndex = -1,
  rowIndex,
  sectionData,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const yachtdata = useSelector(state => state.generatorSlice.yachtData);
  const { setValue } = useFormContext();
  const dispatch = useDispatch();

  const populateCurrentSection = useCallback(
    (item: IOzoneItem) => {
      let input: HTMLDivElement | HTMLInputElement | null = ref.current;
      const fields: Array<keyof IOzoneItem> = [
        "no",
        "loc",
        "type",
        "ref",
        "sub",
        "mass",
        "date",
        "isc", // Initial system charge
        "lubc", // Lubricant charge
        "lubg", // Lubricant grade
      ];
      if (sectionData?.dynamic) {
        if (rowIndex === undefined || sectionIndex === -1) return;
        const cols = sections[sectionIndex].rows
          .slice(rowIndex)
          .filter(r => !Object.values(r.cols ?? {}).some(c => c.headerStyle))
          .reduce(
            (acc, r) => {
              return {
                ...acc,
                ...Object.fromEntries(
                  Object.entries(r.cols ?? {})
                    .filter(
                      ([, col]) =>
                        col.tag && fields.includes(col.tag as keyof IOzoneItem),
                    )
                    .map(([key, col]) => [col.tag, key]),
                ),
              };
            },
            {} as Record<string, string>,
          );
        for (const field of fields) {
          const isDate = field === "date";
          const id = cols[field];
          const value = isDate ? validateDate(item[field]) : item[field];
          if (!id) continue;
          setValue(id, value);
        }
      } else {
        for (const field of fields) {
          const isDate = field === "date";
          input = getNextInput(input, isDate ? "date" : "text");
          if (!input || !(input instanceof HTMLInputElement)) break;
          let value = item[field];
          if (isDate) value = validateDate(value);
          setValue(input.name, value);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [setValue],
  );

  const populateNextSection = useCallback(
    (item: IOzoneItem) => {
      if (rowIndex === undefined || sectionIndex === -1) return;
      const nextSectionIndex = sectionIndex + 1;
      const rowsLength = sections[nextSectionIndex].rows.length;
      const row =
        sections[nextSectionIndex].rows[rowIndex] ??
        sections[nextSectionIndex].rows[0];
      const newRow = getFilledRow(
        (row.cols ?? {}) as Record<string, ICol>,
        item,
      );
      if (rowIndex > rowsLength) {
        const emptyRows = Array(rowIndex - rowsLength).fill(
          getNewEmptyRow((row.cols ?? {}) as Record<string, ICol>),
        );
        dispatch(
          addNewDynamicRow({
            row: [...emptyRows, newRow],
            sectionIndex: nextSectionIndex,
          }),
        );
      } else {
        dispatch(
          addNewDynamicRow({
            row: newRow,
            sectionIndex: nextSectionIndex,
            rowIndex,
          }),
        );
      }
    },
    [dispatch, rowIndex, sectionIndex, sections],
  );

  const handleSelect = useCallback(
    (value: string) => {
      if (!yachtdata?.ozone || !ref.current) return;
      const item = yachtdata.ozone.find(el => el.name === value);
      if (!item) return;
      populateCurrentSection(item);
      if (!sectionData?.dynamic) populateNextSection(item);
    },
    [
      populateCurrentSection,
      populateNextSection,
      yachtdata?.ozone,
      sectionData,
    ],
  );

  const data = useMemo(
    () =>
      yachtdata?.ozone
        ? yachtdata.ozone.map((el: IOzoneItem) => ({
            label: el.name,
            value: el.name,
          }))
        : [],
    [yachtdata?.ozone],
  );

  if (data.length > 0) {
    return (
      <div ref={ref}>
        <CustomSelect
          data={data}
          id={id}
          value={value}
          required={required}
          onChange={handleSelect}
        />
      </div>
    );
  }

  return <CustomInput id={id} value={value} required={required} />;
};

export default DpsEquipInput;
